import React, { useRef, useState, useMemo } from "react";
import { cx } from "../../Util/classHelpers";
import { ChSortIcon, DownloadIcon } from "../Icons/ch-analytics-icons";
import styles from "./chBreakdownTable.module.css";

const COLUMNS = [
    { key: "companyName", header: "Company", sortType: "text" },
    { key: "totalCreated", header: "Created", sortType: "number", metric: "created" },
    { key: "totalDelivered", header: "Delivered", sortType: "number", metric: "delivered" },
    { key: "totalFailed", header: "Failed", sortType: "number", metric: "failed" },
    { key: "totalIncomplete", header: "Incomplete", sortType: "number", metric: "incomplete" },
    { key: "totalDeleted", header: "Deleted", sortType: "number", metric: "deleted" },
    { key: "ontimePercentage", header: "% On-time", sortType: "number" },
    { key: "avgDriveTimeMinutes", header: "Avg. on the way", sortType: "number" },
];

const ChBreakdownTable = ({ rows, loading, activeDrawer, onCellClick, onExport }) => {
    const [sort, setSort] = useState({ key: null, dir: null });

    function handleSort(key) {
        setSort((prev) => {
            if (prev.key !== key) return { key, dir: "desc" };
            if (prev.dir === "desc") return { key, dir: "asc" };
            return { key: null, dir: null };
        });
    }

    const sortedRows = useMemo(() => {
        if (!sort.key || !sort.dir) return rows;

        const col = COLUMNS.find((c) => c.key === sort.key);
        if (!col) return rows;

        const sorted = [...rows].sort((a, b) => {
            const aVal = a[sort.key];
            const bVal = b[sort.key];

            if (col.sortType === "text") {
                const aStr = (aVal || "").toLowerCase();
                const bStr = (bVal || "").toLowerCase();
                if (aStr < bStr) return -1;
                if (aStr > bStr) return 1;
                return 0;
            }

            return (aVal ?? 0) - (bVal ?? 0);
        });

        if (sort.dir === "desc") sorted.reverse();
        return sorted;
    }, [rows, sort]);

    return (
        <div className={styles.tableCard}>
            <div className={styles.sectionTitle}>
                <span>Breakdown by Companies</span>
                <button className={styles.exportBtn} onClick={onExport} title="Export as CSV">
                    <DownloadIcon size={13} />
                    Export
                </button>
            </div>
            <div className={styles.tableScroll}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={cx(
                                        col.key === "companyName" && styles.colCompany,
                                        styles.sortableTh
                                    )}
                                    onClick={() => handleSort(col.key)}
                                >
                                    <span className={styles.thContent}>
                                        <span>{col.header}</span>
                                        <ChSortIcon dir={sort.key === col.key ? sort.dir : null} />
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <tr key={i}>
                                    {Array.from({ length: 8 }).map((_, j) => (
                                        <td key={j}><div className={styles.skelCell} /></td>
                                    ))}
                                </tr>
                            ))
                        ) : sortedRows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.empty}>
                                    No data for selected filters
                                </td>
                            </tr>
                        ) : (
                            sortedRows.map((row) => (
                                <TableRow
                                    key={row.companyId}
                                    row={row}
                                    activeMetric={
                                        activeDrawer && activeDrawer.companyId === row.companyId
                                            ? activeDrawer.metric
                                            : null
                                    }
                                    onCellClick={onCellClick}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TableRow = ({ row, activeMetric, onCellClick }) => {
    return (
        <tr className={styles.tr}>
            <td className={styles.colCompany}>
                <span className={styles.companyName}>{row.companyName || `Company ${row.companyId}`}</span>
            </td>

            <MetricCell value={row.totalCreated} metric="created" row={row}
                active={activeMetric === "created"} onCellClick={onCellClick} />

            <MetricCell value={row.totalDelivered} metric="delivered" row={row}
                active={activeMetric === "delivered"} onCellClick={onCellClick} />

            <MetricCell value={row.totalFailed} metric="failed" row={row}
                active={activeMetric === "failed"} onCellClick={onCellClick} />

            <MetricCell value={row.totalIncomplete} metric="incomplete" row={row}
                active={activeMetric === "incomplete"} onCellClick={onCellClick} />

            <MetricCell value={row.totalDeleted} metric="deleted" row={row}
                active={activeMetric === "deleted"} onCellClick={onCellClick} />

            <td className={styles.pctCell}>
                <span className={styles.pctVal}>{row.ontimePercentage.toFixed(1)}%</span>
            </td>

            <td className={styles.avgCell}>
                {row.avgDriveTimeMinutes.toFixed(0)} min
            </td>
        </tr>
    );
};

const MetricCell = ({ value, metric, row, active, onCellClick, sub }) => {
    const ref = useRef(null);

    function handleClick(e) {
        e.stopPropagation();
        const el = ref.current;
        if (el) {
            const r = document.createElement("span");
            r.className = styles.ripple;
            const rect = el.getBoundingClientRect();
            r.style.left = `${e.clientX - rect.left}px`;
            r.style.top = `${e.clientY - rect.top}px`;
            el.appendChild(r);
            setTimeout(() => r.remove(), 500);
        }
        onCellClick({ companyId: row.companyId, companyName: row.companyName, metric });
    }

    return (
        <td
            ref={ref}
            className={cx(styles.metricCell, styles[`cell_${metric}`], active && styles.activeCell)}
            onClick={handleClick}
        >
            <span className={styles.cellVal}>{value.toLocaleString()}</span>
            {sub && <span className={styles.cellSub}>{sub}</span>}
        </td>
    );
};

export default ChBreakdownTable;
